import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Download, RefreshCw, TrendingUp, Users, Music, PlayCircle, Activity, Mail, ShieldAlert } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardValue, CardSubtitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { api, API_BASE, getAuthToken } from '@/lib/api';
import { formatNumber, formatDate } from '@/lib/utils';

type Period = '7d' | '30d' | '90d' | 'all' | 'custom';

const BRAND_COLORS = ['#C084FC', '#9333EA', '#7E22CE', '#6B21A8', '#581C87', '#A855F7', '#D8B4FE', '#E9D5FF'];

function downloadCsv(type: string) {
  const token = getAuthToken();
  // Forçar download via URL com token query (fallback porque <a download> ignora headers)
  const url = `${API_BASE}/admin/metrics/export?type=${type}`;
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `amuzze-${type}-${Date.now()}.csv`;
      a.click();
    });
}

// `query` é OPCIONAL desde 31/10/2026: a rota de engajamento não aceita
// janela de período e passa a ser chamada sem parâmetro nenhum. Sem o `?`
// aqui, `tsc -b` recusa a chamada — e foi exatamente o que quebrou o build
// do admin em 29/10, sem ninguém perceber, porque `tsc --noEmit` passa.
function useMetric<T = any>(name: string, query: Record<string, string | number | undefined> = {}) {
  return useQuery<{ data: T; fromCache: boolean }>({
    queryKey: [`metric-${name}`, query],
    queryFn: async () => {
      const { data } = await api.get(`/admin/metrics/${name}`, { params: query });
      return data;
    },
  });
}

export function Analytics() {
  const [period, setPeriod] = useState<Period>('30d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const periodQuery = period === 'custom' ? { period, from, to } : { period };

  const overview     = useMetric('overview',     periodQuery);
  const growth       = useMetric<{ series: any[] }>('growth', periodQuery);
  const distAge      = useMetric<{ items: any[] }>('distribution', { type: 'age' });
  const distState    = useMetric<{ items: any[] }>('distribution', { type: 'state' });
  const distProfile  = useMetric<{ items: any[] }>('distribution', { type: 'profile_type' });
  const distGenres   = useMetric<{ items: any[] }>('distribution', { type: 'genres' });
  const distInstr    = useMetric<{ items: any[] }>('distribution', { type: 'instruments' });
  const topTracks    = useMetric<{ items: any[] }>('top',          { type: 'tracks', limit: 10 });
  const topArtists   = useMetric<{ items: any[] }>('top',          { type: 'artists', limit: 10 });
  const topCollabInstr = useMetric<{ items: any[] }>('top',        { type: 'collab_instruments', limit: 10 });
  const customInstr  = useMetric<{ items: any[] }>('custom-instruments', { limit: 20 });
  // 🔑 SEM `periodQuery` de proposito. Esta rota devolve medias sobre toda a
  // base ativa e nao acompanha janela — mandar o periodo fazia o bloco parecer
  // filtrado ao lado de `overview` e `growth`, que mudam de verdade. Numero que
  // nao muda ao lado de numeros que mudam nao se denuncia: ele se faz passar
  // por resposta. A API agora declara `escopo: 'geral'`.
  const engagement   = useMetric('engagement');

  const ov = overview.data?.data as any;
  const eng = engagement.data?.data as any;

  return (
    <Layout
      title="Analytics"
      actions={
        <>
          <Select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
            <option value="all">Tudo</option>
            <option value="custom">Período personalizado</option>
          </Select>
          {period === 'custom' && (
            <>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                     className="h-10 rounded-lg border border-border bg-bg-input px-3 text-sm text-text-primary" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                     className="h-10 rounded-lg border border-border bg-bg-input px-3 text-sm text-text-primary" />
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
            <RefreshCw size={14} /> Atualizar
          </Button>
        </>
      }
    >
      {/* === Cards principais === */}
      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>Total de usuários</CardTitle>
            <Users size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(ov?.users?.total)}</CardValue>
          <CardSubtitle>
            +{formatNumber(ov?.new_in_period)} no período
          </CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>DAU / WAU / MAU</CardTitle>
            <Activity size={18} className="text-brand" />
          </CardHeader>
          <CardValue className="text-2xl">
            {formatNumber(ov?.activity?.dau)} / {formatNumber(ov?.activity?.wau)} / {formatNumber(ov?.activity?.mau)}
          </CardValue>
          <CardSubtitle>Usuários ativos (dia/semana/mês)</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Músicas publicadas</CardTitle>
            <Music size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(ov?.content?.tracks)}</CardValue>
          <CardSubtitle>+ {formatNumber(ov?.content?.clips)} clipes</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Plays (30d)</CardTitle>
            <PlayCircle size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(ov?.plays_30d)}</CardValue>
          <CardSubtitle>{formatNumber(ov?.content?.collab_posts)} colabs ativas</CardSubtitle>
        </Card>
      </section>

      {/* === Distribuição de perfis (mini cards) === */}
      <section className="mt-4 grid grid-cols-3 gap-4">
        <Card>
          <CardTitle>Artistas solo</CardTitle>
          <CardValue className="text-2xl">{formatNumber(ov?.users?.solo)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Bandas</CardTitle>
          <CardValue className="text-2xl">{formatNumber(ov?.users?.bands)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Fãs</CardTitle>
          <CardValue className="text-2xl">{formatNumber(ov?.users?.fans)}</CardValue>
        </Card>
      </section>

      {/* === E-mail confirmado ===
          Dois números, de propósito separados. "Confirmado" é saúde da base;
          "podem receber" é quem a regra realmente autoriza a receber
          newsletter — consentimento E confirmação, não um dos dois.

          A regra está gravada como comentário da coluna
          `users.newsletter_opt_in` no banco (decisão de 16/09/2026), porque
          não existe remetente de newsletter no código: quem for construí-lo
          vai olhar a coluna, não um documento. O motivo não é proteger quem
          assina — é que a reputação de remetente na Resend é compartilhada
          com redefinição de senha e com a autorização de responsável de
          menor, que é obrigação legal.

          Antes disto o número era medido à mão e ficou 14 dias sem ninguém
          olhando. */}
      <section className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>E-mail confirmado</CardTitle>
            <Mail size={18} className="text-brand" />
          </CardHeader>
          <CardValue>
            {ov?.email?.pct_confirmados == null ? '—' : `${ov.email.pct_confirmados}%`}
          </CardValue>
          <CardSubtitle>
            {formatNumber(ov?.email?.confirmados)} de {formatNumber(ov?.email?.total)} contas ativas
          </CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Podem receber newsletter</CardTitle>
            <Mail size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(ov?.email?.enviaveis_newsletter)}</CardValue>
          <CardSubtitle>
            {formatNumber(ov?.email?.consentiram_newsletter)} consentiram — enviar exige também e-mail confirmado
          </CardSubtitle>
        </Card>
      </section>

      {/* === Menores aguardando consentimento ===
          Descoberto em 01/10/2026 por acaso: duas contas de menor estavam
          paradas havia 13 e 7 dias sem nenhum registro de consentimento, e
          não havia número nem aviso que mostrasse isso.

          Não é exposição — o login recusa menor sem consentimento confirmado.
          É uma família que quis entrar e ficou travada entre a tela de
          cadastro e a do responsável, sem nada que reconvide.

          O cartão fica destacado quando há alguém esperando, e some para o
          fundo quando a fila está zerada: número que pisca sempre deixa de
          ser lido. */}
      <section className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Menores aguardando consentimento</CardTitle>
            <ShieldAlert
              size={18}
              className={ov?.menores?.aguardando > 0 ? 'text-danger' : 'text-text-muted'}
            />
          </CardHeader>
          <CardValue className={ov?.menores?.aguardando > 0 ? 'text-danger' : undefined}>
            {formatNumber(ov?.menores?.aguardando)}
          </CardValue>
          <CardSubtitle>
            {formatNumber(ov?.menores?.total)} contas de menor ·{' '}
            {formatNumber(ov?.menores?.confirmados)} com responsável confirmado
            {ov?.menores?.sem_registro > 0 && (
              <> · <strong>{formatNumber(ov.menores.sem_registro)} nunca chegou a pedir</strong></>
            )}
          </CardSubtitle>
        </Card>
      </section>

      {/* === Gráfico de crescimento === */}
      <section className="mt-6">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Novos cadastros por dia</CardTitle>
              <CardSubtitle>Linha = total. Barras = artistas vs fãs.</CardSubtitle>
            </div>
            <TrendingUp size={18} className="text-brand" />
          </CardHeader>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growth.data?.data?.series || []}>
                <defs>
                  <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#C084FC" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#C084FC" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="day" tickFormatter={formatDate} stroke="#737373" fontSize={11} />
                <YAxis stroke="#737373" fontSize={11} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8 }} />
                <Area type="monotone" dataKey="new_users" stroke="#C084FC" fill="url(#grad1)" name="Novos users" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </section>

      {/* === Distribuições (pizzas/barras) === */}
      <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Por faixa etária</CardTitle>
          </CardHeader>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distAge.data?.data?.items || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="bucket" stroke="#737373" fontSize={11} />
                <YAxis stroke="#737373" fontSize={11} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8 }} />
                <Bar dataKey="count" fill="#C084FC" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Por tipo de conta</CardTitle>
          </CardHeader>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={distProfile.data?.data?.items || []}
                  dataKey="count"
                  nameKey="bucket"
                  cx="50%" cy="50%"
                  outerRadius={80}
                  label={(d) => `${d.bucket}: ${d.count}`}
                >
                  {(distProfile.data?.data?.items || []).map((_, i) => (
                    <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top estados</CardTitle>
          </CardHeader>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(distState.data?.data?.items || []).slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis type="number" stroke="#737373" fontSize={11} />
                <YAxis type="category" dataKey="bucket" stroke="#737373" fontSize={11} width={50} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8 }} />
                <Bar dataKey="count" fill="#9333EA" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Gêneros musicais (preferência)</CardTitle>
          </CardHeader>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(distGenres.data?.data?.items || []).slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis type="number" stroke="#737373" fontSize={11} />
                <YAxis type="category" dataKey="bucket" stroke="#737373" fontSize={11} width={100} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8 }} />
                <Bar dataKey="count" fill="#A855F7" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </section>

      {/* === Instrumentos === */}
      <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Instrumentos tocados (top oficiais)</CardTitle>
              <CardSubtitle>Dado-chave pra parcerias com marcas de instrumento</CardSubtitle>
            </div>
          </CardHeader>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(distInstr.data?.data?.items || []).slice(0, 15)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis type="number" stroke="#737373" fontSize={11} />
                <YAxis type="category" dataKey="bucket" stroke="#737373" fontSize={11} width={110} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8 }} />
                <Bar dataKey="count" fill="#C084FC" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Instrumentos procurados (colabs)</CardTitle>
              <CardSubtitle>Tipos mais requisitados em buscas de colaboração</CardSubtitle>
            </div>
          </CardHeader>
          <ul className="space-y-2">
            {(topCollabInstr.data?.data?.items || []).map((item: any, i: number) => (
              <li key={i} className="flex items-center justify-between rounded-lg border border-border bg-bg-input px-3 py-2 text-sm">
                <span className="text-text-primary">{item.instrument}</span>
                <span className="font-semibold text-brand">{formatNumber(item.count)}</span>
              </li>
            ))}
            {!topCollabInstr.data && <li className="text-sm text-text-muted">Carregando…</li>}
          </ul>
        </Card>
      </section>

      {/* === Custom instruments (monitor) === */}
      <section className="mt-6">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Instrumentos "Outro" mais digitados</CardTitle>
              <CardSubtitle>Use isso pra avaliar quais merecem virar opção oficial</CardSubtitle>
            </div>
          </CardHeader>
          {(customInstr.data?.data?.items?.length ?? 0) === 0 ? (
            <p className="text-sm text-text-muted">Nenhum instrumento custom registrado ainda.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {(customInstr.data?.data?.items || []).map((item: any, i: number) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-bg-input px-3 py-2 text-sm">
                  <span className="truncate text-text-primary">{item.instrument}</span>
                  <span className="ml-2 font-semibold text-brand">{item.users_count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* === Engajamento === */}
      {/* O rotulo nao e enfeite: ele e o que impede o bloco de ser lido como
          se respondesse ao filtro de periodo escolhido la em cima. */}
      <h2 className="mt-6 text-sm font-medium text-zinc-400">
        Engajamento{' '}
        <span className="font-normal text-zinc-500">
          — médias sobre toda a base, não acompanham o período
        </span>
      </h2>
      <section className="mt-2 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardTitle>Tracks por user</CardTitle>
          <CardValue className="text-2xl">{eng?.avg_tracks_per_user || '—'}</CardValue>
          <CardSubtitle>média</CardSubtitle>
        </Card>
        <Card>
          <CardTitle>Followers por user</CardTitle>
          <CardValue className="text-2xl">{eng?.avg_followers_per_user || '—'}</CardValue>
          <CardSubtitle>média</CardSubtitle>
        </Card>
        <Card>
          <CardTitle>Likes por track</CardTitle>
          <CardValue className="text-2xl">{eng?.avg_likes_per_track || '—'}</CardValue>
          <CardSubtitle>média</CardSubtitle>
        </Card>
        <Card>
          <CardTitle>Plays por track</CardTitle>
          <CardValue className="text-2xl">{eng?.avg_plays_per_track || '—'}</CardValue>
          <CardSubtitle>média</CardSubtitle>
        </Card>
      </section>

      {/* === Rankings === */}
      <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Top 10 músicas (plays)</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={() => downloadCsv('top-tracks')}>
              <Download size={14} /> CSV
            </Button>
          </CardHeader>
          <ul className="space-y-1">
            {(topTracks.data?.data?.items || []).map((t: any, i: number) => (
              <li key={t.id} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                <div>
                  <div className="text-text-primary">{i + 1}. {t.title}</div>
                  <div className="text-xs text-text-muted">@{t.username} · {t.genre || 'sem gênero'}</div>
                </div>
                <span className="font-semibold text-brand">{formatNumber(t.plays_count)} plays</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Top 10 artistas (seguidores)</CardTitle>
            </div>
          </CardHeader>
          <ul className="space-y-1">
            {(topArtists.data?.data?.items || []).map((a: any, i: number) => (
              <li key={a.id} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
                <div>
                  <div className="text-text-primary">{i + 1}. {a.display_name}</div>
                  <div className="text-xs text-text-muted">@{a.username} · {a.profile_type}</div>
                </div>
                <span className="font-semibold text-brand">{formatNumber(a.followers_count)} fãs</span>
              </li>
            ))}
          </ul>
        </Card>
      </section>

      {/* === Export geral === */}
      <section className="mt-6">
        <Card>
          <CardTitle>Exportar dados completos (CSV)</CardTitle>
          <CardSubtitle>Pra apresentações comerciais, parcerias e análises externas (uso interno apenas)</CardSubtitle>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadCsv('users-summary')}>
              <Download size={14} /> Sumário de usuários
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadCsv('top-tracks')}>
              <Download size={14} /> Top 1000 músicas
            </Button>
          </div>
        </Card>
      </section>
    </Layout>
  );
}
